import React from 'react';
import {
  makeStyles,
  createStyles,
  Button,
  ButtonProps,
  ButtonGroup,
  Theme,
  CircularProgress,
  useMediaQuery,
  PopoverOrigin,
  ButtonGroupProps,
} from '@material-ui/core';
import { useTheme } from '@material-ui/styles';
import classNames from 'classnames';
import { PlusIcon, ChevronDownIcon } from 'src/components/Icons';
import { BaseMenu } from 'src/components/Dropdowns/BaseMenu';
import { HoverBackground } from 'src/theme/colors';
import ColorUtils from 'src/utils/ColorUtils';
import { Action } from 'src/components/Dropdowns';
import { MenuVariant } from 'src/constants';

interface StyleProps {
  isSecondaryButton: boolean;
}

const useCreateButtonStyles = makeStyles((theme: Theme) =>
  createStyles({
    createButton: {
      padding: `0 ${theme.spacing(1)}px`,
    },
    createButtonActive: {
      backgroundColor: ({ isSecondaryButton }: StyleProps) =>
        // background color of button based on button variant (primary or secondary)
        // when dropdown menu is open
        isSecondaryButton
          ? HoverBackground
          : ColorUtils.GetColorDarknessShades(theme.palette.primary.main)
              .midDark,
    },
    buttonProgress: {
      position: 'absolute',
      top: '50%',
      left: '50%',
      marginTop: -15,
      marginLeft: -15,
    },
  }),
);

export interface DropDownItemMoreAction {
  name: string;
  icon: React.ReactElement;
  // when clicked on sub action
  // we will get parent menu item information in param
  onClick: (action: Action) => void;
}

export interface DropDownOption {
  value: string;
  label: string;
  // these will be the sub actions we want on the the dropdown menu item
  subActions?: DropDownItemMoreAction[];
}

export interface MainOption {
  label: string;
  icon: JSX.Element;
  disabled?: boolean;
  tooltip?: string;
}

export interface CreateButtonProps {
  htmlId: string;
  caretOptions: DropDownOption[];
  dropDownTitle: string;
  actionOptionTitle: string;
  mainOptions: Array<MainOption>;
  headerOptions?: Array<MainOption>;
  onClickOption: (option: any) => void;
  onClickOptionAction: () => void;
  isUploading?: boolean;
  iconOnly?: boolean;
  forceShowLabel?: boolean;
  anchorOrigin?: PopoverOrigin;
  transformOrigin?: PopoverOrigin;
  buttonGroupProps?: ButtonGroupProps;
  dropdownVariant?: MenuVariant;
}

export type CreateButtonCombinedProps = ButtonProps &
  Partial<CreateButtonProps>;

export const CreateButton: React.FC<CreateButtonCombinedProps> = React.memo(
  ({
    children,
    htmlId,
    caretOptions,
    mainOptions,
    headerOptions,
    dropDownTitle,
    actionOptionTitle,
    isUploading = false,
    onClickOption,
    onClickOptionAction,
    iconOnly = false,
    forceShowLabel = false,
    anchorOrigin,
    transformOrigin,
    onClick,
    buttonGroupProps,
    dropdownVariant = 'wide',
    ...rest
  }) => {
    const isSecondaryButton = buttonGroupProps?.color === 'secondary';
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
    const classes = useCreateButtonStyles({ isSecondaryButton });
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
      setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
      setAnchorEl(null);
    };

    const formatOptions = (options: MainOption[]) =>
      options.map(({ label, icon, disabled, tooltip }) => ({
        key: label,
        onClick: () => onClickOption?.(label),
        name: label,
        icon,
        isDisabled: disabled,
        tooltipLabel: tooltip,
      }));

    const getFormattedHeaderActions = React.useCallback(() => {
      if (headerOptions) {
        return formatOptions(headerOptions);
      }
      return [];
    }, [headerOptions]);

    const getFormattedActions = React.useCallback(() => {
      if (mainOptions) {
        return formatOptions(mainOptions);
      }

      if (caretOptions) {
        return caretOptions?.map(option => ({
          key: `${option.value || option.label}`,
          onClick: () => onClickOption?.(option.value),
          name: option.label,
          subActions: option.subActions,
        }));
      }
      return [];
    }, [caretOptions, mainOptions, onClickOption]);

    return (
      <div>
        <ButtonGroup
          disableRipple
          disableFocusRipple
          variant="contained"
          color="primary"
          aria-label="split button"
          {...buttonGroupProps}
        >
          <Button
            color="primary"
            className={classNames(classes.createButton, {
              [classes.createButtonActive]: open, // when popover is open add hover styles
            })}
            id={htmlId}
            {...(!iconOnly
              ? { startIcon: !isMobile ? <PlusIcon /> : null }
              : {})}
            onClick={onClick || handleClick}
            disabled={isUploading}
            {...rest}
          >
            {(isMobile || iconOnly) && !forceShowLabel ? (
              <PlusIcon />
            ) : (
              children
            )}
            {isUploading && (
              <CircularProgress className={classes.buttonProgress} size={30} />
            )}
          </Button>
          {caretOptions && (
            <Button
              color="primary"
              size="small"
              aria-label="caret options"
              aria-controls="caret-options-menu"
              aria-haspopup="true"
              className={classNames(classes.createButton, {
                [classes.createButtonActive]: open,
              })}
              onClick={handleClick}
            >
              <ChevronDownIcon />
            </Button>
          )}
        </ButtonGroup>
        <BaseMenu
          variant={dropdownVariant}
          menuTitle={dropDownTitle}
          addOptionLabel={actionOptionTitle}
          onAddOption={onClickOptionAction}
          menuProps={{
            id: 'options-menu',
            anchorEl,
            getContentAnchorEl: null,
            keepMounted: true,
            open,
            onClose: handleClose,
            anchorOrigin: {
              vertical: 'bottom',
              horizontal: 'right',
              ...anchorOrigin,
            },
            transformOrigin: {
              vertical: 'top',
              horizontal: 'right',
              ...transformOrigin,
            },
          }}
          headerActions={getFormattedHeaderActions()}
          actions={getFormattedActions()}
          preventClickEventPropagation
        />
      </div>
    );
  },
);
